import { InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Input, InputNumber, Select, Tooltip, Typography } from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import { Trans, useTranslation } from 'react-i18next';

import {
  RequestJobPostingDetail,
  UserCollection,
  VixtingAffiliate,
  VixtingCompany,
  VixtingDepartment,
  VixtingJobRole,
  VixtingUnitBusiness,
} from '../types';
import { currencyFormatter, currencyParser } from '../utils';
import Form from './Form';
import RichEditor from './RichEditor';

interface GeneralRequestJobPostingFormProps {
  enableVixtingAdmission?: boolean;
  vixtingCompanies: VixtingCompany[];
  vixtingJobRoles: VixtingJobRole[];
  vixtingDepartments: VixtingDepartment[];
  vixtingAffiliates?: VixtingAffiliate[];
  vixtingUnitBusinesses?: VixtingUnitBusiness[];
  form: FormInstance<unknown>;
  users: UserCollection[];
  disableCommentaryField?: boolean;
  disableReplacedEmployeeNameField?: boolean;
  enableVixtingUnitBusiness?: boolean;
}

export interface GeneralRequestJobPostingFormValues {
  requestType: RequestJobPostingDetail['type'];
  positions: number;
  employeeNameBeingReplaced?: string;
  name: string;
  startSalaryRange?: string | null;
  endSalaryRange?: string | null;
  branch?: string;
  description?: string;
  relatedUsers?: UserCollection['id'][];
  annotation?: string;
  vixtingCompany?: string;
  vixtingAffiliate?: string;
  vixtingJobRole?: string;
  vixtingDepartment?: string;
  vixtingUnitBusiness?: string;
}

const GeneralRequestJobPostingForm = ({
  form,
  users,
  enableVixtingAdmission,
  vixtingCompanies = [],
  vixtingJobRoles = [],
  vixtingDepartments = [],
  vixtingAffiliates = [],
  vixtingUnitBusinesses = [],
  disableCommentaryField = false,
  disableReplacedEmployeeNameField = false,
  enableVixtingUnitBusiness = false,
}: GeneralRequestJobPostingFormProps) => {
  const { t } = useTranslation();

  const endSalaryRangeValue = Form.useWatch<number | string | undefined>('endSalaryRange', form);

  const selectedCompanyValue = Form.useWatch<string>('vixtingCompany', form);
  const selectedJobRoleValue = Form.useWatch<string>('vixtingJobRole', form);

  const validateSalaryRange = async () => {
    try {
      await form.validateFields(['startSalaryRange']);
    } catch (err) {
      console.warn(
        'O valor inicial da faixa salarial não pode ser maior ou igual que o valor final.'
      );
    }
  };

  const resetCompanyRelatedFields = (selectedCompany: string) => {
    form.setFieldsValue({
      vixtingAffiliate: selectedCompany || undefined,
      vixtingJobRole: undefined,
      vixtingDepartment: undefined,
      vixtingUnitBusiness: undefined,
    });
  };
  const resetJobRoleRelatedFields = () => {
    form.setFieldsValue({
      vixtingDepartment: undefined,
    });
  };

  return (
    <>
      <Form.Col span={12}>
        <Form.Row>
          <Form.Col span={12}>
            <Form.Item
              name="requestType"
              label={t('Tipo de requisição')}
              rules={[{ required: true }]}
            >
              <Select>
                <Select.Option value="STAFF_INCREASE">
                  <Trans>Aumento de Quadro</Trans>
                </Select.Option>
                <Select.Option value="SUBSTITUTION">
                  <Trans>Substituição</Trans>
                </Select.Option>
              </Select>
            </Form.Item>
          </Form.Col>
          <Form.Col span={12}>
            <Form.Item
              name="positions"
              label={t('Quantidade de posições')}
              rules={[{ required: true }, { type: 'number', min: 1 }]}
            >
              <InputNumber min={1} max={2147483647} style={{ width: '100%' }} />
            </Form.Item>
          </Form.Col>
        </Form.Row>
        <Form.Item noStyle shouldUpdate={(prev, next) => prev.requestType !== next.requestType}>
          {(form) =>
            form.getFieldValue('requestType') === 'SUBSTITUTION' && (
              <Form.Row>
                <Form.Col span={12}>
                  <Form.Item
                    name="employeeNameBeingReplaced"
                    label={t('Nome do substituído')}
                    rules={[{ required: form.getFieldValue('requestType') === 'SUBSTITUTION' }]}
                  >
                    <Input disabled={disableReplacedEmployeeNameField} />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
            )
          }
        </Form.Item>
        <Form.Item name="name" label={t('Nome da requisição')} rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label={t('Faixa salarial')}>
          <Form.Row>
            <Form.Col span={12}>
              <Form.Item
                name="startSalaryRange"
                noStyle
                rules={[
                  {
                    validator(_, value: number | undefined) {
                      if (!value && !endSalaryRangeValue) {
                        return Promise.resolve();
                      }

                      if (
                        (value && value < 0) ||
                        (endSalaryRangeValue && Number(endSalaryRangeValue) < 0)
                      ) {
                        return Promise.reject(new Error(t('O valor mínimo é 0')));
                      }

                      // if no value was inserted, it is valid
                      if (
                        value === undefined ||
                        (endSalaryRangeValue && value < Number(endSalaryRangeValue))
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          t(
                            'O valor inicial da faixa salarial não pode ser maior ou igual que o valor final.'
                          )
                        )
                      );
                    },
                  },
                ]}
              >
                <InputNumber<string | number>
                  prefix="R$"
                  style={{ width: '100%' }}
                  min={0}
                  max={2147483647}
                  placeholder={t('Mínimo')}
                  step={100}
                  formatter={(value) => currencyFormatter(value)}
                  parser={(displayValue) => currencyParser(displayValue)}
                  onChange={validateSalaryRange}
                  stringMode
                />
              </Form.Item>
            </Form.Col>
            <Form.Col span={12}>
              <Form.Item name="endSalaryRange" noStyle>
                <InputNumber<string | number>
                  prefix="R$"
                  style={{ width: '100%' }}
                  min={1}
                  max={2147483647}
                  placeholder={t('Máximo')}
                  step={100}
                  formatter={(value) => currencyFormatter(value)}
                  parser={(displayValue) => currencyParser(displayValue)}
                  onChange={validateSalaryRange}
                  stringMode
                />
              </Form.Item>
            </Form.Col>
          </Form.Row>
        </Form.Item>
        {!enableVixtingUnitBusiness && (
          <Form.Row>
            <Form.Col span={24}>
              <Form.Item name="branch" label={t('Unidade')}>
                <Input />
              </Form.Item>
            </Form.Col>
          </Form.Row>
        )}
        {enableVixtingAdmission && (
          <>
            <Form.Row>
              <Form.Col span={12}>
                <Form.Item name="vixtingCompany" label={t('Empresa')}>
                  <Select
                    showSearch
                    allowClear
                    filterSort={(optionA, optionB) =>
                      (optionA?.label ?? '').localeCompare(optionB?.label ?? '')
                    }
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={resetCompanyRelatedFields}
                    data-testid="select-vixting-company"
                    options={vixtingCompanies.map((company) => ({
                      key: company.id,
                      value: `${company.id}`,
                      label: `${company.name}`,
                      'data-testid': `select-vixting-company-${company.id}`,
                      children: <span title={company.id}>{`${company.name}`}</span>,
                    }))}
                  />
                </Form.Item>
              </Form.Col>
              <Form.Col span={12}>
                <Form.Item name="vixtingAffiliate" label={t('Filial')}>
                  <Select
                    showSearch
                    allowClear
                    filterSort={(optionA, optionB) =>
                      (optionA?.label ?? '').localeCompare(optionB?.label ?? '')
                    }
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    disabled={!selectedCompanyValue}
                    data-testid="select-vixting-affiliate"
                    options={vixtingAffiliates.map((affiliate) => ({
                      key: affiliate.id,
                      value: `${affiliate.id}`,
                      'data-testid': `select-vixting-affiliate-${affiliate.id}`,
                      label: `${affiliate.fantasyName}`,
                    }))}
                  />
                </Form.Item>
              </Form.Col>
            </Form.Row>
            {enableVixtingUnitBusiness && (
              <Form.Row>
                <Form.Col span={24}>
                  <Form.Item name="vixtingUnitBusiness" label={t('Unidade de Negócio')}>
                    <Select
                      showSearch
                      allowClear
                      filterSort={(optionA, optionB) =>
                        (optionA?.label ?? '').localeCompare(optionB?.label ?? '')
                      }
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      disabled={!selectedCompanyValue}
                      data-testid="select-vixting-unit-business"
                      options={vixtingUnitBusinesses.map((unitBusiness) => ({
                        key: unitBusiness.id,
                        value: `${unitBusiness.id}`,
                        'data-testid': `select-vixting-unit-business-${unitBusiness.id}`,
                        label: `${unitBusiness.name}`,
                      }))}
                    />
                  </Form.Item>
                </Form.Col>
              </Form.Row>
            )}
            <Form.Row>
              <Form.Col span={12}>
                <Form.Item name="vixtingJobRole" label={t('Cargo')}>
                  <Select
                    showSearch
                    allowClear
                    disabled={enableVixtingAdmission && !selectedCompanyValue}
                    filterSort={(optionA, optionB) =>
                      (optionA?.label ?? '').localeCompare(optionB?.label ?? '')
                    }
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={resetJobRoleRelatedFields}
                    data-testid="select-vixting-job-role"
                    options={vixtingJobRoles.map((jobRole) => ({
                      key: jobRole.id,
                      value: jobRole.id.toString(),
                      label: `${jobRole.id} - ${jobRole.name}`,
                      'data-testid': `select-vixting-job-role-${jobRole.id}`,
                    }))}
                  />
                </Form.Item>
              </Form.Col>
              <Form.Col span={12}>
                <Form.Item name="vixtingDepartment" label={t('Departamento')}>
                  <Select
                    showSearch
                    allowClear
                    disabled={enableVixtingAdmission && !selectedJobRoleValue}
                    filterSort={(optionA, optionB) =>
                      (optionA?.label ?? '').localeCompare(optionB?.label ?? '')
                    }
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    data-testid="select-vixting-department"
                    options={vixtingDepartments.map((department) => ({
                      key: department.id,
                      label: department.name,
                      value: department.id.toString(),
                      'data-testid': `department-${department.id}`,
                    }))}
                  />
                </Form.Item>
              </Form.Col>
            </Form.Row>
            <Form.Row>
              <Form.Col span={24}>
                <Alert
                  message={
                    <span style={{ fontSize: '13px' }}>
                      <Trans>
                        Caso o cargo ou departamento desejado não esteja disponível, entre em
                        contato com o time de SST da Vixting pelo e-mail
                      </Trans>{' '}
                      <a
                        href="mailto:sst@vixting.com.br?subject=Solicitação de vinculação de cargo/departamento"
                        style={{ fontSize: '13px' }}
                      >
                        sst@vixting.com.br
                      </a>{' '}
                      <Trans>para solicitar a vinculação.</Trans>
                    </span>
                  }
                  type="info"
                  showIcon
                  closable
                />
              </Form.Col>
            </Form.Row>
          </>
        )}
      </Form.Col>
      <Form.Col span={18}>
        <Form.Item name="description" label={t('Descrição da vaga')}>
          <RichEditor />
        </Form.Item>
      </Form.Col>
      <Form.Col span={10}>
        <Form.Item
          name="relatedUsers"
          label={
            <Typography.Text>
              <Trans>Envolvidos</Trans>{' '}
              <Tooltip
                title={t(
                  'Adicione os usuários que estarão envolvidos na vaga. Eles serão notificados sobre o status da requisição, incluindo a criação da vaga se a aprovação for total, ou caso alguma etapa seja reprovada'
                )}
              >
                <InfoCircleOutlined />
              </Tooltip>
            </Typography.Text>
          }
        >
          <Select
            allowClear
            mode="multiple"
            showSearch
            filterOption={(input, option) =>
              (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
            }
          >
            {users.map((user) =>
              user.full_name ? (
                <Select.Option key={user.id} value={user.id}>
                  {user.full_name}
                </Select.Option>
              ) : (
                <Select.Option key={user.id} value={user.id}>
                  {user.username}
                </Select.Option>
              )
            )}
          </Select>
        </Form.Item>
        <Form.Item label={t('Registrar comentário')} name="annotation">
          <Input.TextArea maxLength={4000} rows={6} showCount disabled={disableCommentaryField} />
        </Form.Item>
      </Form.Col>
    </>
  );
};

export default GeneralRequestJobPostingForm;
